'use no memo';

/* eslint-disable react-compiler/react-compiler */

/* eslint-disable react/jsx-props-no-spreading */
import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
} from 'react';

import { useLocation } from 'react-router';

import { safeLazy } from '@local/utils';

import breakpoints from 'helpers/breakpoints';
import { logAnalyticsEvent, ANALYTICS_EVENT_NAME } from 'helpers/loggers';
import { getSourcePage, MODAL_TYPE } from 'helpers/modals';
import { getPopupPosition } from 'helpers/yelp';

import usePlatform from 'hooks/usePlatform';

import ModalWrapper from 'components/ModalWrapper';
import ModalWrapperStyles from 'components/ModalWrapper/styles.module.scss';

import { RestaurantContext } from './RestaurantContext';

const MODAL_COMPONENTS = {
  ACCOUNT: safeLazy(() => import('components/ModalWrapper/AccountModal'), null),
  AUTH_SHARED: safeLazy(
    () => import('components/ModalWrapper/AuthSharedModal'),
    null
  ),
  BAG: safeLazy(() => import('components/ModalWrapper/BagModal'), null),
  CATEGORY: safeLazy(
    () => import('components/ModalWrapper/CategoryModal'),
    null
  ),
  CATERING_FULFILLMENT: safeLazy(
    () => import('components/ModalWrapper/CateringFulfillmentModal'),
    null
  ),
  CONFIRM_ACCOUNT: safeLazy(
    () => import('components/ModalWrapper/ConfirmAccountModal'),
    null
  ),
  DIALOG: safeLazy(() => import('components/ModalWrapper/DialogModal'), null),
  DOWNLOAD_APP: safeLazy(
    () => import('components/ModalWrapper/DownloadAppModal'),
    null
  ),
  FULFILLMENT_DETAILS: safeLazy(
    () => import('components/ModalWrapper/FulfillmentDetailsModal'),
    null
  ),
  HELP: safeLazy(() => import('components/ModalWrapper/HelpModal'), null),
  IMPACT_SCORE: safeLazy(
    () => import('components/ModalWrapper/ImpactScoreModal'),
    null
  ),
  ITEM_EXPIRED: safeLazy(
    () => import('components/ModalWrapper/ItemExpiredModal'),
    null
  ),
  REWARDS: safeLazy(() => import('components/ModalWrapper/RewardsModal'), null),
  MENU_ITEM: safeLazy(
    () => import('components/ModalWrapper/MenuItemModal'),
    null
  ),
  ORDERING_UNAVAILABLE: safeLazy(
    () => import('components/ModalWrapper/OrderingUnavailableModal'),
    null
  ),
  POLLING: safeLazy(() => import('components/ModalWrapper/PollingModal'), null),
  POST_PURCHASE_IMPACT: safeLazy(
    () => import('components/ModalWrapper/PostPurchaseImpactModal'),
    null
  ),
  PROMO: safeLazy(() => import('components/ModalWrapper/PromoModal'), null),
  TABLE: safeLazy(() => import('components/ModalWrapper/TableModal'), null),
  TIME_UNAVAILABLE: safeLazy(
    () => import('components/ModalWrapper/TimeUnavailableModal'),
    null
  ),
  TIP_ADJUSTMENT: safeLazy(
    () => import('components/ModalWrapper/TipAdjustmentModal'),
    null
  ),
  TIP: safeLazy(() => import('components/ModalWrapper/TipModal'), null),
};

interface ModalProviderProps {
  children: React.ReactNode;
}

type ModalEntry = { component: string; props: object };

export type ModalContextProps = {
  isModalOpen: boolean;
  modalComponent?: string;
  modalProps?: Record<string, unknown>;
  modalStack?: ModalEntry[];
  openModal: (
    component: string,
    props?: object,
    options?: { stacked?: boolean }
  ) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextProps>({
  isModalOpen: false,
  modalStack: [],
  openModal: () => {},
  closeModal: () => {},
});

function ModalProvider({ children }: ModalProviderProps) {
  const [modalStack, setModalStack] = useState<ModalEntry[]>([]);

  const location = useLocation();
  const { isYelpPlatform } = usePlatform();

  const component = modalStack[modalStack.length - 1]?.component ?? '';
  const componentProps = modalStack[modalStack.length - 1]?.props ?? {};
  const isModalOpen = modalStack.length > 0;

  const isMobile = window.innerWidth < breakpoints.xsMax;
  const path = location.pathname;
  const sourcePageArgs = {
    path,
    isMobile,
  };

  const { triggerModal, setTriggerModal } = useContext(RestaurantContext);

  // When running the Yelp platform, we have to measure the height of the modal dialog
  // being displayed, and send a message to Yelp.  In return, they will tell us what the
  // top value should be (where the dialog should be positioned relative to the top of
  // the iframe the app is rendered within).
  // Ref:  https://docs.developer.yelp.com/docs/iframe-api-specification#popup-interactions
  useEffect(() => {
    let observer: ResizeObserver | undefined;

    const observeModalForPositioning = () => {
      if (!isYelpPlatform || !isModalOpen) return;

      const modalElement = document.querySelector(
        `.${ModalWrapperStyles.modalWrapper}`
      );

      if (!modalElement) return;

      // Lazy content mounts at height 0, then shifts again when an item image
      // loads. Re-post on every nonzero height so Yelp re-centers the final
      // size instead of an early, too-short measurement.
      observer = new ResizeObserver(() => {
        if (modalElement.clientHeight) {
          getPopupPosition(modalElement.clientHeight);
        }
      });
      observer.observe(modalElement);
    };

    observeModalForPositioning();

    return () => observer?.disconnect();
  }, [isModalOpen]);

  function openModal(
    componentType: string,
    props?: object,
    { stacked = false }: { stacked?: boolean } = {}
  ) {
    // do not attempt to open a different modal if AUTH is currently open
    if (!componentType || component === MODAL_TYPE.auth) return;

    logAnalyticsEvent({
      eventName: ANALYTICS_EVENT_NAME.openModal,
      attributes: {
        modal_type: `${componentType}`,
        source: getSourcePage(sourcePageArgs),
      },
    });

    document.body.style.overflowY = 'hidden'; // no scrolling behind modal

    const entry: ModalEntry = { component: componentType, props: props || {} };
    if (stacked) {
      setModalStack((prev) => [...prev, entry]);
    } else {
      setModalStack([entry]);
    }
  }

  useEffect(() => {
    if (triggerModal.modalType) {
      openModal(triggerModal.modalType, triggerModal.modalProps);
      setTriggerModal({});
    }
  }, [triggerModal]);

  function closeModal() {
    if (!isYelpPlatform) {
      logAnalyticsEvent({
        eventName: ANALYTICS_EVENT_NAME.closeModal,
        attributes: {
          source: getSourcePage(sourcePageArgs),
          modal_type: `${component}`,
        },
      });
    }

    if (modalStack.length <= 1) {
      document.body.style.overflowY = ''; // enable scrolling
      document.getElementById('root')?.removeAttribute('inert'); // reallow focusing when modal is closed
    }

    setModalStack((prev) => prev.slice(0, -1));
  }

  useEffect(() => {
    // don't auto-close auth modal on route change (inquiry)
    const isAuthModal = component === MODAL_TYPE.auth;

    if (location.pathname && isModalOpen && !isAuthModal) {
      // Clear the entire stack on route change
      if (!isYelpPlatform) {
        logAnalyticsEvent({
          eventName: ANALYTICS_EVENT_NAME.closeModal,
          attributes: {
            source: getSourcePage(sourcePageArgs),
            modal_type: `${component}`,
          },
        });
      }
      document.body.style.overflowY = '';
      document.getElementById('root')?.removeAttribute('inert');
      setModalStack([]);
    }
  }, [path]);

  const values = useMemo(
    () => ({
      isModalOpen,
      modalProps: componentProps as Record<string, unknown>,
      modalComponent: component,
      modalStack,
      openModal,
      closeModal,
    }),
    [modalStack]
  );

  return (
    <ModalContext.Provider value={values}>{children}</ModalContext.Provider>
  );
}

/**
 * we separate the modal context and the modal renderer so that things
 * that modals depend on can also themselves open modals. For example,
 * the ItemExpired modal depends on the ValidateOrderProvider which itself
 * depends on the ModalContext.
 *
 * The ModalRenderer should go as deep in the tree as possible, within all other
 * providers. The ModalContext can go wherever, but probably above most providers.
 */
export function ModalRenderer() {
  const { modalStack = [] } = useContext(ModalContext);

  if (!modalStack.length) return null;

  return (
    <>
      {modalStack.map((entry, index) => {
        const ModalComponent =
          MODAL_COMPONENTS[entry.component as keyof typeof MODAL_COMPONENTS];
        if (!ModalComponent) return null;

        const isBackground = index < modalStack.length - 1;

        return (
          // eslint-disable-next-line
          <ModalWrapper key={index} background={isBackground}>
            <ModalComponent {...entry.props} />
          </ModalWrapper>
        );
      })}
    </>
  );
}

export { ModalContext, ModalProvider };
